/**
 * One document: the rendered page on the left, what was read from it on the right.
 *
 * The two panes are linked by the region index. Selecting a region in the
 * inspector highlights its box on the page and vice versa, which is what makes
 * an extracted value checkable against the drawing it came from.
 *
 * The page is marked at two levels, because a sheet carries several drawings and
 * they interleave. The outer, dashed box is one drawing - "SECTION A-A", the
 * parts list - and it may overlap its neighbour, which on a real sheet they do.
 * The solid boxes inside it are the parts that were actually cropped and read,
 * each tight on its own block of content. The inspector uses the same two levels,
 * so a value is read under the drawing it belongs to rather than under a region
 * number that says nothing about which drawing that is.
 *
 * A document analyzed before the grouping existed has no groups, and then both
 * panes fall back to the single flat level they had.
 */
import { useEffect, useMemo, useRef, useState } from "react";

import type { BBox, DocumentDetail as Detail, Position } from "../api";
import { Badge, Button, Icon, IconButton, Switch } from "../components/primitives";
import type { ValueEntry } from "../format";
import { asTable, flatten, formatDate, imageSrc, regionClass, shortName } from "../format";

const ZOOMS = [50, 75, 100, 150, 200];

/** One of the sheet's drawings, with the parts that were read inside it. */
interface DrawingGroup {
  /** Stable key: the group number, or the region's own index when ungrouped. */
  key: string;
  /** 1-based number shown on the sheet and in the inspector. */
  number: number;
  label: string;
  kind: string;
  bbox: BBox | null;
  /** 0-based page the drawing is on. */
  page: number;
  members: { position: Position; index: number }[];
}

interface DocumentDetailProps {
  document: Detail;
  onBack: () => void;
  /** Download this document's extraction as CSV. */
  onExport: () => void;
  /** True while the export is being built, so it cannot be started twice. */
  exporting: boolean;
  /** Open the assistant pinned to this document. */
  onAsk: () => void;
  /** True when the assistant is already pinned to it, so the button says so. */
  asking: boolean;
}

/**
 * Collect the regions into the drawings they belong to.
 *
 * Order follows the regions, so the drawings appear in the order they were
 * detected and a region never moves relative to its own siblings. A region with
 * no group becomes a drawing of one, which is what makes the rest of this screen
 * indifferent to whether the record was analyzed with grouping or without.
 */
function groupsOf(positions: Position[]): DrawingGroup[] {
  const byKey = new Map<string, DrawingGroup>();
  positions.forEach((position, index) => {
    const key = keyOf(position, index);
    const existing = byKey.get(key);
    if (existing) {
      existing.members.push({ position, index });
      return;
    }
    byKey.set(key, {
      key,
      number: byKey.size + 1,
      label: position.group_label?.trim() || "",
      kind: position.group_kind?.trim() || "",
      bbox: position.group_bbox ?? null,
      page: (position.source_page ?? 1) - 1,
      members: [{ position, index }],
    });
  });
  return [...byKey.values()];
}

/** The drawing a region belongs to, as a key - its group, or itself when ungrouped. */
function keyOf(position: Position, index: number): string {
  return position.group != null ? `g${position.group}` : `r${index}`;
}

/** A box's share of the page, for deciding which of two overlapping ones is on top. */
function boxArea(bbox: BBox | null | undefined): number {
  if (!bbox) return 0;
  return Math.max(0, bbox[2] - bbox[0]) * Math.max(0, bbox[3] - bbox[1]);
}

/** What a drawing is called in the inspector and on its box. */
function groupTitle(group: DrawingGroup): string {
  if (group.label) return group.label;
  if (group.kind) return group.kind.replace(/_/g, " ");
  return group.members.length > 1 ? "Untitled drawing" : "Unnamed region";
}

export function DocumentDetail({
  document,
  onBack,
  onExport,
  exporting,
  onAsk,
  asking,
}: DocumentDetailProps) {
  const [zoom, setZoom] = useState<number | "fit">("fit");
  const [overlay, setOverlay] = useState(true);
  const [active, setActive] = useState<number | null>(null);
  // Hover is tracked so pointing at a row lights up its box and vice versa. On a
  // sheet where boxes overlap, that is the only way to tell which frame belongs
  // to the row you are reading without clicking and undoing it.
  const [hover, setHover] = useState<number | null>(null);
  /**
   * The drawing whose parts are shown, and the one under the pointer.
   *
   * The sheet is marked one drawing at a time. Every part of every drawing drawn
   * at once was the thing nobody could read: eighteen numbered rectangles, some
   * of them crossing, and no way to see why the split into R12, R13 and R14 is a
   * split at all. A drawing needs no justification - it is a thing on the sheet
   * with a name - so that is the level the page shows, and the parts inside one
   * appear when you go into it.
   */
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [hoverGroup, setHoverGroup] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  // Selecting in one pane scrolls the other to match. Both panes scroll - the
  // sheet vertically in its stage, the inspector down a long list of regions -
  // and a selection you cannot see is the same as no selection.
  const boxRefs = useRef(new Map<number, HTMLDivElement>());
  const rowRefs = useRef(new Map<number, HTMLDivElement>());

  const positions = useMemo(() => document.data.positions ?? [], [document.data.positions]);
  const header = useMemo(() => flatten(document.data.header), [document.data.header]);
  const groups = useMemo(() => groupsOf(positions), [positions]);
  const pages = document.pages;
  const hasPages = pages.length > 0;

  // Nothing to say about grouping when every drawing holds exactly one region:
  // the second level would be a frame drawn around a single box and a heading
  // repeating the row under it.
  const nested = groups.some((group) => group.members.length > 1);

  /** The drawing the selected region belongs to, so its frame lights up too. */
  const activeGroup =
    active === null
      ? null
      : (groups.find((group) => group.members.some((member) => member.index === active))?.key ??
        null);

  /**
   * The one drawing whose parts are drawn on the sheet.
   *
   * Deliberately one, and the pointer wins: a drawing stays open until another is
   * opened, but pointing at a different one previews it and pointing away puts
   * the opened one back. Falls through to whichever drawing holds the selected
   * region, so selecting a row in the panel opens its drawing without a second
   * click.
   */
  const shownGroup = hoverGroup ?? openGroup ?? activeGroup;

  // source_page is 1-based in the extraction; the viewer indexes from 0.
  const regionsOnPage = positions
    .map((position, index) => ({ position, index }))
    .filter(({ position }) => (position.source_page ?? 1) - 1 === page)
    // Only the opened drawing's parts are drawn. Everything else on the sheet is
    // represented by its drawing's own box.
    .filter(({ position, index }) => keyOf(position, index) === shownGroup)
    // Largest first, so the smallest box is painted last and sits on top. Boxes
    // on a drawing do overlap - a scale bar inside the plan, a dimension strip
    // along its edge - and in document order a big box laid over a small one
    // takes every click meant for it, leaving the small one unselectable. By
    // area, the small one is always clickable and the big one keeps every part
    // of itself that nothing covers.
    .sort((one, other) => boxArea(other.position.bbox) - boxArea(one.position.bbox));
  const groupsOnPage = groups
    .filter((group) => group.page === page && group.bbox)
    .sort((one, other) => boxArea(other.bbox) - boxArea(one.bbox));

  const zoomIndex = zoom === "fit" ? -1 : ZOOMS.indexOf(zoom);

  /** Select a region and bring its page forward. */
  const focus = (index: number, sourcePage: number | undefined) => {
    setActive(index);
    // Selecting a region with the overlay switched off used to highlight nothing
    // at all - the one case where clicking a row in the panel appeared to do
    // nothing. Asking to see a region is asking for the boxes.
    setOverlay(true);
    // Opening the drawing it belongs to, because that is what puts its box on
    // the sheet: only one drawing's parts are drawn at a time.
    const owner = groups.find((group) => group.members.some((member) => member.index === index));
    if (owner) setOpenGroup(owner.key);
    const target = (sourcePage ?? 1) - 1;
    if (target >= 0 && target < pages.length) setPage(target);
  };

  // `nearest` rather than `center`: it does nothing when the thing is already
  // visible, so selecting a region you can see does not jerk either pane.
  useEffect(() => {
    if (active === null) return;
    const options: ScrollIntoViewOptions = { block: "nearest", inline: "nearest" };
    rowRefs.current.get(active)?.scrollIntoView(options);
    boxRefs.current.get(active)?.scrollIntoView(options);
  }, [active, page]);

  return (
    <section className="screen">
      <div className="screen__head" style={{ alignItems: "center" }}>
        {/* The same way out that Compare has. The breadcrumb says where you are;
            the arrow is the control people reach for without reading it. */}
        <IconButton icon="arrow_back" label="Back to the library" onClick={onBack} />
        <div className="detail__title">
          <div className="dac-crumbs">
            <a onClick={onBack} style={{ cursor: "pointer", fontWeight: 600 }}>
              Documents
            </a>
            <span className="dac-crumbs__sep">/</span>
            <span className="u-truncate">{shortName(document.name, 52)}</span>
          </div>
          <h1
            className="u-truncate"
            style={{ fontSize: 20, lineHeight: 1.3, fontWeight: 600, margin: "5px 0 0" }}
          >
            {document.name}
          </h1>
        </div>
        {/*
          The first action on the sheet you are looking at: it opens the assistant
          already pinned to this drawing, which is the only way a question about
          "the top view" is unambiguous. Without it the panel opens on the whole
          library and the answer arrives with four other documents attached.
        */}
        <Button variant="primary" size="sm" icon="forum" onClick={onAsk}>
          {asking ? "Asking about this drawing" : "Ask about this drawing"}
        </Button>
        {/*
          Beside Delete rather than in the inspector's toolbar: this exports the
          whole document, not the panel the toolbar sits on - the Copy JSON button
          in there is the one that acts on what is displayed.
        */}
        <Button size="sm" icon="download" onClick={onExport} disabled={exporting}>
          {exporting ? "Exporting..." : "Export CSV"}
        </Button>
      </div>

      <div className="screen__body">
        <div className="detail">
          <div className="viewer">
            <div className="viewer__bar">
              <IconButton
                icon="zoom_out"
                label="Zoom out"
                size="sm"
                onClick={() =>
                  setZoom(zoom === "fit" ? 75 : ZOOMS[Math.max(zoomIndex - 1, 0)])
                }
              />
              <span className="u-mono" style={{ minWidth: 40, textAlign: "center", fontSize: 12 }}>
                {zoom === "fit" ? "Fit" : `${zoom}%`}
              </span>
              <IconButton
                icon="zoom_in"
                label="Zoom in"
                size="sm"
                onClick={() =>
                  setZoom(zoom === "fit" ? 100 : ZOOMS[Math.min(zoomIndex + 1, ZOOMS.length - 1)])
                }
              />
              <IconButton icon="fit_screen" label="Fit to width" size="sm" onClick={() => setZoom("fit")} />
              <span className="dac-topbar__divider" style={{ background: "var(--border-hairline)", height: 18 }} />
              {hasPages && (
                <Switch label="Detected regions" checked={overlay} onChange={setOverlay} />
              )}
              <span className="u-spacer" />
              {pages.length > 1 && (
                <span className="u-row">
                  <IconButton
                    icon="chevron_left"
                    label="Previous page"
                    size="sm"
                    disabled={page === 0}
                    onClick={() => setPage((current) => Math.max(0, current - 1))}
                  />
                  <span className="screen__caption">
                    Page {page + 1} / {pages.length}
                  </span>
                  <IconButton
                    icon="chevron_right"
                    label="Next page"
                    size="sm"
                    disabled={page >= pages.length - 1}
                    onClick={() => setPage((current) => Math.min(pages.length - 1, current + 1))}
                  />
                </span>
              )}
              {pages.length === 1 && <span className="screen__caption">Page 1 / 1</span>}
            </div>

            <div className="viewer__stage">
              {hasPages ? (
                <div
                  className="viewer__page"
                  style={{
                    width: zoom === "fit" ? "100%" : `${Math.round(7.2 * zoom)}px`,
                    maxWidth: zoom === "fit" ? 860 : "none",
                  }}
                >
                  <img src={imageSrc(pages[page])} alt={`Page ${page + 1}`} />
                  {/*
                    Drawings first, so their frames sit under the boxes they
                    enclose and a click lands on the part rather than on the
                    frame around it. They are not clickable for the same reason:
                    nothing was read from a drawing's box, so there is nothing
                    for selecting one to show.
                  */}
                  {/*
                    The sheet's own level: one box per drawing, named. These need
                    no explaining - "this is SECTION A-A" - which is exactly what
                    a numbered sub-rectangle could not manage. Largest first again,
                    so a small drawing lying inside a big one stays clickable.
                  */}
                  {overlay &&
                    groupsOnPage.map((group) => {
                      const [x0, y0, x1, y1] = group.bbox as BBox;
                      const single = group.members[0];
                      const open = shownGroup === group.key;
                      return (
                        <div
                          key={group.key}
                          className={`drawingbox ${activeGroup === group.key ? "drawingbox--on" : ""} ${open ? "drawingbox--open" : ""}`.trim()}
                          style={{
                            left: `${x0 * 100}%`,
                            top: `${y0 * 100}%`,
                            width: `${(x1 - x0) * 100}%`,
                            height: `${(y1 - y0) * 100}%`,
                          }}
                          onClick={() => {
                            // A drawing read in one piece has nothing to open, so
                            // its box selects the reading directly.
                            if (group.members.length === 1) focus(single.index, single.position.source_page);
                            else setOpenGroup((current) => (current === group.key ? null : group.key));
                          }}
                          onMouseEnter={() => setHoverGroup(group.key)}
                          onMouseLeave={() =>
                            setHoverGroup((current) => (current === group.key ? null : current))
                          }
                          role="button"
                          aria-label={`Drawing ${group.number}: ${groupTitle(group)}`}
                          title={
                            group.members.length === 1
                              ? groupTitle(group)
                              : `${groupTitle(group)} - ${group.members.length} regions, click to open`
                          }
                        >
                          <span className="drawingbox__tag">
                            D{group.number}
                            {/* Dropped on a phone, where the names of neighbouring
                                drawings run into each other at that scale. */}
                            <span className="drawingbox__name">
                              {" "}
                              {groupTitle(group)}
                              {group.members.length > 1 ? ` (${group.members.length})` : ""}
                            </span>
                          </span>
                        </div>
                      );
                    })}
                  {overlay &&
                    regionsOnPage.map(({ position, index }) => {
                      const box = position.bbox;
                      if (!box) return null;
                      const [x0, y0, x1, y1] = box;
                      const lit = active === index || hover === index;
                      return (
                        <div
                          key={index}
                          ref={(node) => {
                            if (node) boxRefs.current.set(index, node);
                            else boxRefs.current.delete(index);
                          }}
                          className={`${regionClass(box, active === index)} ${hover === index ? "region--hot" : ""}`.trim()}
                          style={{
                            left: `${x0 * 100}%`,
                            top: `${y0 * 100}%`,
                            width: `${(x1 - x0) * 100}%`,
                            height: `${(y1 - y0) * 100}%`,
                          }}
                          onClick={() => focus(index, position.source_page)}
                          onMouseEnter={() => setHover(index)}
                          onMouseLeave={() => setHover((current) => (current === index ? null : current))}
                          role="button"
                          aria-label={`Region ${index + 1}${position.label ? `: ${position.label}` : ""}`}
                          title={[position.group_label, position.label].filter(Boolean).join(" - ")}
                        >
                          {/*
                            The number alone said nothing about why a box is a box
                            of its own - "why is there an R3 here" had no answer on
                            the sheet, only in the panel. The part's own name
                            answers it, and it appears on the box you are pointing
                            at rather than on all of them at once, which would
                            bury the drawing under labels.
                          */}
                          <span className="region__tag">
                            R{index + 1}
                            {lit && position.label ? ` · ${position.label}` : ""}
                          </span>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div style={{ maxWidth: 640, margin: "0 auto" }}>
                  <div className="filecard" style={{ background: "#fff" }}>
                    <Icon name="picture_as_pdf" size={24} className="u-muted" />
                    <div className="screen__caption">
                      No page preview was archived for this document. The values read from it are in the
                      panel on the right.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <aside className="inspector">
            <div className="inspector__head">
              <span style={{ flex: 1 }}>Extracted data</span>
              <Badge
                tone={positions.length > 0 ? "success" : "neutral"}
                label={
                  nested
                    ? `${groups.length} ${groups.length === 1 ? "drawing" : "drawings"} - ${positions.length} regions`
                    : `${positions.length} ${positions.length === 1 ? "region" : "regions"}`
                }
              />
              <IconButton
                icon="content_copy"
                label="Copy JSON"
                size="sm"
                onClick={() => navigator.clipboard?.writeText(JSON.stringify(document.data, null, 2))}
              />
            </div>

            <div className="inspector__body">
              <div className="inspector__section">
                <div className="eyebrow">Document</div>
                <div className="kv">
                  <span className="kv__k">Analyzed</span>
                  <span className="kv__v">{formatDate(document.created)}</span>
                </div>
                <div className="kv">
                  <span className="kv__k">Regions</span>
                  <span className="kv__v">{document.n_positions}</span>
                </div>
                <div className="kv">
                  <span className="kv__k">Values</span>
                  <span className="kv__v">{document.n_fields}</span>
                </div>
                {header.map((entry) => (
                  <Value key={entry.label} entry={entry} />
                ))}
              </div>

              {groups.map((group) => (
                <div className="drawing" key={group.key}>
                  {nested && (
                    <div
                      className={`drawing__head ${activeGroup === group.key ? "drawing__head--on" : ""} ${shownGroup === group.key ? "drawing__head--open" : ""}`.trim()}
                      // Opens the drawing on the sheet rather than selecting its
                      // first region: the heading is about the drawing, and what
                      // the reader wants from it is to see where it is and what
                      // it is divided into.
                      onClick={() => {
                        setOpenGroup((current) => (current === group.key ? null : group.key));
                        const first = group.members[0];
                        const target = (first.position.source_page ?? 1) - 1;
                        if (target >= 0 && target < pages.length) setPage(target);
                      }}
                      onMouseEnter={() => setHoverGroup(group.key)}
                      onMouseLeave={() =>
                        setHoverGroup((current) => (current === group.key ? null : current))
                      }
                      role="button"
                      tabIndex={0}
                    >
                      <span className="drawing__tag">D{group.number}</span>
                      <span className="drawing__title u-truncate">{groupTitle(group)}</span>
                      <span className="u-spacer" />
                      <span className="drawing__count">
                        {group.members.length} {group.members.length === 1 ? "region" : "regions"}
                      </span>
                    </div>
                  )}
                  {group.members.map(({ position, index }) => (
                    <RegionGroup
                      key={index}
                      index={index}
                      position={position}
                      active={active === index}
                      hot={hover === index}
                      nested={nested}
                      onFocus={() => focus(index, position.source_page)}
                      onHover={(on) => {
                        setHover((current) => (on ? index : current === index ? null : current));
                        setHoverGroup((current) =>
                          on ? group.key : current === group.key ? null : current,
                        );
                      }}
                      innerRef={(node) => {
                        if (node) rowRefs.current.set(index, node);
                        else rowRefs.current.delete(index);
                      }}
                    />
                  ))}
                </div>
              ))}

              {positions.length === 0 && (
                <div className="inspector__section screen__caption">
                  The extraction returned no regions for this document.
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function RegionGroup({
  index,
  position,
  active,
  hot,
  nested,
  onFocus,
  onHover,
  innerRef,
}: {
  index: number;
  position: Position;
  active: boolean;
  /** True while the pointer is over this row or over its box on the sheet. */
  hot: boolean;
  /** True when this region sits under a drawing heading, which indents it. */
  nested: boolean;
  onFocus: () => void;
  onHover: (on: boolean) => void;
  innerRef: (node: HTMLDivElement | null) => void;
}) {
  const fields = flatten(position.fields);
  const drawing = flatten(position.drawing);

  return (
    <div
      ref={innerRef}
      className={`regiongroup ${active ? "regiongroup--on" : ""} ${hot ? "regiongroup--hot" : ""} ${nested ? "regiongroup--nested" : ""}`.trim()}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
    >
      {/* The whole header is the target, not just the tag: a row you can point
          at to light up its box on the sheet has to be easy to hit. */}
      <div className="regiongroup__head" onClick={onFocus} role="button" tabIndex={0}>
        {/*
          The tag is the whole heading. A title here could only be one of the field
          values listed immediately below it, so it restated a row rather than adding
          anything; "R2" already says which region this is. Under a drawing heading
          the part's own name does add something - it says which piece of that
          drawing this is - so it is shown there and nowhere else.
        */}
        <span className="regiongroup__tag">R{index + 1}</span>
        {nested && position.label && (
          <span className="regiongroup__label u-truncate">{position.label}</span>
        )}
        <span className="u-spacer" />
        {position.bbox ? null : <Badge tone="warning" label="No box" />}
      </div>
      {/*
        Both groups are labelled, not just the drawing. The extraction keeps them
        apart for a reason - fields are the filled labels read off the form or
        table, drawing is what was read from the drawing itself - and an unlabelled
        run of values above a "Drawing" heading gave no way to tell which kind you
        were looking at. The labels stay generic because what goes inside either
        group is defined by the account's prompts, not by this screen.
      */}
      <div className="regiongroup__body">
        {fields.length > 0 && (
          <>
            <div className="regiongroup__group">Fields</div>
            {fields.map((entry) => (
              <Value key={entry.label} entry={entry} mono />
            ))}
          </>
        )}
        {drawing.length > 0 && (
          <>
            <div className="regiongroup__group">Drawing</div>
            {drawing.map((entry) => (
              <Value key={entry.label} entry={entry} mono />
            ))}
          </>
        )}
        {fields.length === 0 && drawing.length === 0 && (
          <div className="screen__caption">Nothing was read from this region.</div>
        )}
      </div>
    </div>
  );
}

/**
 * One label and its value, with a list laid out one entry per line.
 *
 * Commas were doing two jobs at once and could not be told apart: a nine-point
 * welding specification came out as one wrapped paragraph whose own "1.", "2."
 * and "3." sat mid-sentence, indistinguishable from the commas inside a note. A
 * line each makes the count visible - nine notes look like nine - and a hanging
 * indent keeps a wrapped line from reading as the next entry.
 *
 * No bullet glyph. What a sheet numbers, it numbers itself, and a disc in front
 * of "1. Welding to EN ISO 15614-1" would be a second marker for the same thing.
 */
function Value({ entry, mono = false }: { entry: ValueEntry; mono?: boolean }) {
  const valueClass = `kv__v ${mono ? "kv__v--mono" : ""}`.trim();

  // A parts list, a take-off or a revision block comes back as rows of cells, and
  // it is a table on the sheet. Rendered as one here it is usable as the thing it
  // is - a reader scans the QTY column - where the same rows as prose are a wall
  // of part numbers. It breaks out of the label/value grid because a table needs
  // the panel's whole width, and the label goes above it.
  const table = asTable(entry.lines);
  if (table) {
    return (
      <div className="vtable-block">
        <div className="vtable-block__label">{entry.label}</div>
        <div className="vtable-wrap">
          <table className="vtable">
            {table.head && (
              <thead>
                <tr>
                  {table.head.map((cell, index) => (
                    <th key={index}>{cell}</th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {table.rows.map((row, index) => (
                <tr key={index} className={row.total ? "vtable__total" : undefined}>
                  {row.cells.map((cell, cellIndex) => (
                    <td key={cellIndex}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (entry.lines.length <= 1) {
    return (
      <div className="kv">
        <span className="kv__k">{entry.label}</span>
        <span className={valueClass}>{entry.lines[0] ?? ""}</span>
      </div>
    );
  }
  return (
    <div className="kv">
      <span className="kv__k">{entry.label}</span>
      <div className={`${valueClass} kv__lines`}>
        {entry.lines.map((line, index) => (
          <span className="kv__line" key={index}>
            {line}
          </span>
        ))}
      </div>
    </div>
  );
}
